// === DeletableEdge === React Flow custom edge with a hover-revealed trashcan.
//
// Round 25 Item 0: users couldn't delete a trigger edge with Backspace (the
// canvas key handler is scoped to scene boxes, not React Flow internals). To
// keep the deletion gesture local to the workflow panel without re-plumbing
// global key listeners, this edge renders the standard smooth-step path plus
// a small absolutely-positioned trashcan icon at the edge midpoint. The icon
// is hidden by default and fades in on hover so the graph stays uncluttered.
//
// Trashcan affordance is rendered ONLY when edge.data.triggerId is set —
// trigger-derived edges (correct / wrong / branching alternatives) carry the
// id, while synthetic Start/End edges, default next-edges, and the
// timeline-frame "in sequence" + tl-next auto-edges leave it unset. That
// keeps auto-generated structure undeletable; only authored triggers can be
// removed from the graph.

import React, { useState, useEffect, useRef } from "react";
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath } from "@xyflow/react";

function DeletableEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  label,
  markerEnd,
  style,
  data,
}) {
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Only trigger-derived edges expose data.triggerId + data.onDelete. Without
  // both, the trashcan is suppressed (synthetic / auto-generated edges).
  const canDelete = !!(data && data.triggerId && typeof data.onDelete === "function");

  // Inline edge-label editing. Double-click the label span to enter edit mode;
  // blur (or Enter) commits the value via data.onSetLabel(id, value). The
  // trigger's underlying data is NOT mutated — onSetLabel writes to the
  // canvas_workflow.edgeLabels UI slice. Empty input on commit means "clear
  // override" (delegated to onSetLabel("") which the parent maps to
  // clearEdgeLabel).
  const customLabel = data && typeof data.customLabel === "string" ? data.customLabel : null;
  const displayedLabel = customLabel != null && customLabel !== "" ? customLabel : label;
  const canEditLabel = !!(data && typeof data.onSetLabel === "function");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (data && typeof data.onDelete === "function" && data.triggerId) {
      data.onDelete(data.triggerId);
    }
  };

  const handleLabelDoubleClick = (e) => {
    if (!canEditLabel) return;
    e.preventDefault();
    e.stopPropagation();
    setDraft(displayedLabel != null ? String(displayedLabel) : "");
    setEditing(true);
  };

  const commitEdit = () => {
    if (!editing) return;
    setEditing(false);
    if (canEditLabel) {
      data.onSetLabel(id, draft);
    }
  };

  const handleInputBlur = () => {
    commitEdit();
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEdit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setEditing(false);
    }
  };

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          className="canvas-wf-edge-overlay"
          // The label renderer reparents the children into a screen-space layer;
          // these coordinates put the controls at the edge midpoint regardless
          // of zoom/pan. `pointer-events: all` is required because the parent
          // overlay defaults to `none` to let edges pass clicks through.
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: "all",
          }}
        >
          {editing ? (
            <input
              ref={inputRef}
              type="text"
              className="canvas-wf-edge-overlay__label-input"
              data-testid={`edge-label-input-${id}`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              // Stop React Flow from picking up the click as a pane-deselect.
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          ) : displayedLabel != null && displayedLabel !== "" ? (
            <span
              className="canvas-wf-edge-overlay__label"
              data-testid={`edge-label-${id}`}
              onDoubleClick={handleLabelDoubleClick}
              title={canEditLabel ? "Double-click to edit label" : undefined}
            >
              {displayedLabel}
            </span>
          ) : canEditLabel ? (
            // No derived label and no override — still allow a double-click
            // gesture on a tiny placeholder so authors can name an unlabeled edge.
            <span
              className="canvas-wf-edge-overlay__label canvas-wf-edge-overlay__label--empty"
              data-testid={`edge-label-${id}`}
              onDoubleClick={handleLabelDoubleClick}
              title="Double-click to add label"
            >
              &nbsp;
            </span>
          ) : null}
          {canDelete ? (
            <button
              type="button"
              className="canvas-wf-edge-overlay__trash"
              aria-label="Delete trigger"
              title="Delete trigger"
              data-testid={`edge-trash-${id}`}
              onClick={handleDelete}
            >
              <i className="material-icons">delete</i>
            </button>
          ) : null}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

export default DeletableEdge;
